'use client'

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { getAuthToken } from '@/lib/auth-client'
import { getMe } from '@/lib/api-client'

const ACTIVE_REVALIDATION_MS = 10 * 60 * 1000
const TOKEN_POLL_MS = 2000

/**
 * Validates session on app load, token changes, and periodic active use.
 * When token exists, calls GET /api/auth/me. On 401/SESSION_REVOKED,
 * apiFetch handles redirect to /login?reason=session_revoked.
 */
export function SessionValidator() {
  const location = useLocation()
  const lastValidatedTokenRef = useRef<string | null>(null)
  const lastValidatedAtRef = useRef(0)
  const activeSinceLastValidationRef = useRef(false)
  const validatingTokenRef = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (location.pathname.startsWith('/admin')) return undefined

    const isAuthCallback = () => window.location.pathname === '/auth/callback'

    const validateToken = (token: string, force = false) => {
      if (isAuthCallback()) return
      if (validatingTokenRef.current === token) return
      if (!force && lastValidatedTokenRef.current === token) return

      validatingTokenRef.current = token

      getMe(token)
        .then(() => {
          lastValidatedTokenRef.current = token
          lastValidatedAtRef.current = Date.now()
          activeSinceLastValidationRef.current = false
        })
        .catch(() => {
          // 401 handled by apiFetch; other errors ignored (token present = allow)
        })
        .finally(() => {
          if (validatingTokenRef.current === token) {
            validatingTokenRef.current = null
          }
        })
    }

    const checkToken = () => {
      const token = getAuthToken()
      if (!token) {
        lastValidatedTokenRef.current = null
        lastValidatedAtRef.current = 0
        activeSinceLastValidationRef.current = false
        return
      }

      if (token !== lastValidatedTokenRef.current) {
        validateToken(token, true)
      }
    }

    const markActive = () => {
      activeSinceLastValidationRef.current = true
    }

    const maybeRevalidateActiveSession = () => {
      if (!activeSinceLastValidationRef.current) return
      const token = getAuthToken()
      if (!token || token !== lastValidatedTokenRef.current) {
        checkToken()
        return
      }
      if (Date.now() - lastValidatedAtRef.current >= ACTIVE_REVALIDATION_MS) {
        validateToken(token, true)
      }
    }

    checkToken()

    const tokenPollId = window.setInterval(checkToken, TOKEN_POLL_MS)
    const activeRevalidationId = window.setInterval(
      maybeRevalidateActiveSession,
      ACTIVE_REVALIDATION_MS,
    )
    const activityEvents: Array<keyof WindowEventMap> = [
      'focus',
      'click',
      'keydown',
      'mousemove',
      'scroll',
      'touchstart',
    ]

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, markActive, { passive: true })
    })

    return () => {
      window.clearInterval(tokenPollId)
      window.clearInterval(activeRevalidationId)
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markActive)
      })
    }
  }, [location.pathname])

  return null
}
